import { ChangeDetectionStrategy, Component, inject, Inject, OnInit, Optional } from '@angular/core';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';

import { TranslateModule } from '@ngx-translate/core';
import { ButtonModule, IconModule } from '@pu/ui';
import { SimpleInputComponentModule } from '@pu/forms';
import { FormBuilder } from '@ngneat/reactive-forms';
import { provideNgxMask } from 'ngx-mask';
import { SigninWithCodeData } from '@pu/models';
import { ActionsSubject, Store } from '@ngrx/store';
import { AuthActions } from '@pu/store';
import { ofType } from '@ngrx/effects';
import { filter, tap } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DIALOG_DATA, DialogRef, DialogService } from '@pu/services';

import { TfaResetComponent } from '../tfa-reset/tfa-reset.component';

@UntilDestroy()
@Component({
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslateModule, ButtonModule, IconModule, SimpleInputComponentModule],
  providers: [provideNgxMask()],
  selector: 'pu-tfa',
  templateUrl: './tfa.component.html',
  styleUrls: ['./tfa.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TfaComponent implements OnInit {
  // TODO: If inject these inside constructor, we get runtime error that theses things are undefined.
  //  @UntilDestroy() does smth with constructor, so Angular can't resolve dependencies.
  //  Change @UntilDestroy() to Angular native takeUntilDestroyed() after update to 16v.
  private _dialogRef = inject(DialogRef);
  private _dialog = inject(DialogService);
  token: string;
  isLoading = false;

  form = this._fb.group({
    code: ['', Validators.required],
  });
  errorDictionary: Record<string, string> = {
    apiValidation: 'errors.invalid_two_factor_code',
  };

  constructor(
    private _fb: FormBuilder,
    private _store: Store,
    private _actionsSubject: ActionsSubject,
    @Optional() @Inject(DIALOG_DATA) public data: Pick<SigninWithCodeData, 'token'>,
  ) {
    this.token = data.token;
  }

  ngOnInit() {
    this._actionsSubject
      .pipe(
        ofType(AuthActions.verifyCode.succeededAction),
        tap(() => {
          this.isLoading = false;
          this._dialogRef.close();
        }),
        untilDestroyed(this),
      )
      .subscribe();

    this._actionsSubject
      .pipe(
        ofType(AuthActions.verifyCode.failedAction),
        tap(() => (this.isLoading = false)),
        filter(({ error }) => error.code === 'invalid_two_factor_code'),
        tap(() => this.form.controls.code.setErrors({ apiValidation: true })),
        untilDestroyed(this),
      )
      .subscribe();
  }

  verify(): void {
    this.isLoading = true;
    this._store.dispatch(AuthActions.verifyCode.action({ payload: { ...this.form.value, token: this.token } }));
  }

  openResetDialog() {
    this._dialogRef.close();
    this._dialog.open(TfaResetComponent, { token: this.token });
  }
}
